import React from "react";
import { Box, Button, Card, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs, { Dayjs } from "dayjs";
import FilterSelect from "./FilterSelect";
import { DateType, DateRange, RecordType } from "Models/filter-types";
import { Pipeline } from "Models/pipeline";

interface FilterBarProps {
  recordType: RecordType;
  dateType: DateType;
  dateRange: DateRange;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  pipelines?: Pipeline[];
  pipelineId?: string;
  onRecordTypeChange: (type: RecordType) => void;
  onDateTypeChange: (type: DateType) => void;
  onDateRangeChange: (range: DateRange) => void;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  onPipelineChange?: (id: string) => void;
  onApplyFilters: () => void;
  loading: boolean;
}

const FilterBar: React.FC<FilterBarProps> = ({
  recordType,
  dateType,
  dateRange,
  startDate,
  endDate,
  pipelines,
  pipelineId,
  onRecordTypeChange,
  onDateTypeChange,
  onDateRangeChange,
  onStartDateChange,
  onEndDateChange,
  onPipelineChange,
  onApplyFilters,
  loading,
}) => {
  return (
    <Card
      variant="outlined"
      sx={{ p: 3, my: 2, ml: 2, mr: 3, bgcolor: "#FFFFFF" }}
    >
      <Grid container spacing={2} alignItems="center" wrap="nowrap">
        {/* Record Type */}
        <FilterSelect<RecordType>
          label="Record Type"
          value={recordType}
          options={Object.values(RecordType)}
          onChange={onRecordTypeChange}
        />

        {/* Date Type */}
        <FilterSelect<DateType>
          label="Date Type"
          value={dateType}
          options={Object.values(DateType)}
          onChange={onDateTypeChange}
        />

        {/* Date Range */}
        <FilterSelect<DateRange>
          label="Date Range"
          value={dateRange}
          options={Object.values(DateRange)}
          onChange={onDateRangeChange}
        />

        {/* Custom date pickers — appear right after Date Range when Custom is selected */}
        {dateRange === DateRange.CUSTOM && (
          <>
            <Grid item>
              <DatePicker
                selected={startDate?.toDate() || null}
                onChange={(date: Date | null) =>
                  onStartDateChange(date ? dayjs(date) : null)
                }
                dateFormat="yyyy-MM-dd"
                customInput={
                  <TextField
                    sx={{ mt: 2.5, width: "100%" }}
                    label="Start Date"
                    size="small"
                  />
                }
              />
            </Grid>
            <Grid item>
              <DatePicker
                selected={endDate?.toDate() || null}
                onChange={(date: Date | null) =>
                  onEndDateChange(date ? dayjs(date) : null)
                }
                dateFormat="yyyy-MM-dd"
                customInput={
                  <TextField
                    sx={{ mt: 2.5, width: "100%" }}
                    label="End Date"
                    size="small"
                  />
                }
              />
            </Grid>
          </>
        )}

        {/* Pipeline — deals only, always at the end */}
        {recordType === RecordType.DEALS && pipelines && pipelines.length > 0 && (
          <Grid item xs={10} sm={6} md={4} lg={3}>
            <InputLabel>Pipeline</InputLabel>
            <FormControl fullWidth size="small">
              <Select
                value={pipelineId ?? pipelines[0].sourcePipelineId}
                onChange={(e) => onPipelineChange?.(e.target.value)}
              >
                {pipelines.map((p) => (
                  <MenuItem key={p.sourcePipelineId} value={p.sourcePipelineId}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
        <Button
          variant="contained"
          type="button"
          color="primary"
          sx={{ fontWeight: 700 }}
          onClick={onApplyFilters}
          disabled={loading}
        >
          Apply Filters
        </Button>
      </Box>
    </Card>
  );
};

export default FilterBar;
